import { Component } from '@theme/component';

/**
 * XHR-based GET to bypass Okendo's fetch interceptor
 * @param {string} url
 * @returns {Promise<Object|null>}
 */
const fetchJSON = (url) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error('Invalid JSON'));
        }
      } else {
        resolve(null);
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send();
  });

/**
 * Rewrites an Okendo media URL to a given pixel dimension
 * @param {string} url
 * @param {number} size
 * @returns {string}
 */
const okendoImageSize = (url, size) => {
  if (!url) return '';
  return url.replace(/\?d=\d+x\d+(&crop=center)?/, `?d=${size}x${size}`);
};

/** SVG arrow for slideshow controls */
const ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8.5 3.5L12.5 7.5M12.5 7.5L8.5 11.5M12.5 7.5H2.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** SVG play icon for video thumbnails */
const PLAY_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none"><path d="M12.25 6.89861C12.9167 7.28351 12.9167 8.24576 12.25 8.63066L4 13.3938C3.33333 13.7787 2.5 13.2976 2.5 12.5278V3.00149C2.5 2.23169 3.33333 1.75057 4 2.13547L12.25 6.89861Z" fill="currentColor" transform="translate(0.75, 0)"/></svg>`;

/** SVG close icon */
const CLOSE_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.3811 1.437L1.3535 10.4646M10.4936 10.4646L1.466 1.437" stroke="currentColor"/></svg>`;

/**
 * @typedef {Object} ProductReviewsRefs
 * @property {HTMLElement} aggregateNumber
 * @property {HTMLElement} aggregateStars
 * @property {HTMLElement} reviewCount
 * @property {HTMLElement} recommend
 * @property {HTMLElement} mediaGallery
 * @property {HTMLElement} activeFilters
 * @property {HTMLElement} activeFiltersList
 * @property {HTMLElement} reviewsList
 * @property {HTMLElement} showMore
 * @property {HTMLButtonElement} showMoreBtn
 * @property {HTMLElement} emptyState
 * @property {HTMLElement} loading
 * @property {HTMLElement} ratingStatus
 * @property {HTMLElement} mediaStatus
 * @property {HTMLElement} verifiedStatus
 * @property {HTMLElement} sortStatus
 */

/**
 * @extends {Component<ProductReviewsRefs>}
 */
class ProductReviews extends Component {
  /** @type {{ rating: number|null, media: string|null, verified: boolean|null }} */
  filters = { rating: null, media: null, verified: null };

  /** @type {Record<string, string>} */
  t = {};

  constructor() {
    super();
    this.allReviews = [];
    this.displayedReviews = [];
    this.sortedFiltered = [];
    this.currentSort = 'date';
    this.currentOrder = 'desc';
    this.bodyTruncateLength = 300;
    this.#boundClosePanel = this.#closePanelOnOutsideClick.bind(this);
    /** @type {import('swiper').default|null} */
    this.lightboxSwiper = null;
    /** @type {HTMLDialogElement|null} */
    this.lightboxDialog = null;
    /** All media items collected for lightbox navigation */
    this.allMediaItems = [];
    /** Popover color scheme class */
    this.popoverColorClass = '';
  }

  connectedCallback() {
    super.connectedCallback();

    const subscriberId = (this.dataset.subscriberId || '').trim();
    const productId = (this.dataset.productId || '').trim();
    const perPage = parseInt(this.dataset.perPage, 10) || 5;

    if (!subscriberId || !productId) {
      if (this.refs.loading) this.refs.loading.hidden = true;
      return;
    }

    this.subscriberId = subscriberId;
    this.productId = productId;
    this.perPage = perPage;
    this.apiBase = `https://api.okendo.io/v1/stores/${subscriberId}/products/shopify-${productId}`;

    this.popoverColorClass = this.dataset.popoverColorScheme || '';
    this.bodyTruncateLength = parseInt(this.dataset.bodyTruncate, 10) || 300;

    try {
      this.t = JSON.parse(this.dataset.translations || '{}');
    } catch {
      this.t = {};
    }
    this.showMediaGallery = this.dataset.showMediaGallery !== 'false';
    this.showReviewMedia = this.dataset.showReviewMedia !== 'false';
    this.enableLightbox = this.dataset.enableLightbox !== 'false';
    document.addEventListener('click', this.#boundClosePanel);
    this.#fetchAll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#boundClosePanel);
    this.#destroyLightbox();
    this.gallerySwiper?.destroy(true, true);
  }

  /** @type {(e: MouseEvent) => void} */
  #boundClosePanel;

  async #fetchAll() {
    try {
      const allData = await this.#fetchReviewsBatch(100);
      if (!allData) throw new Error('No response from reviews API');

      this.allReviews = allData.reviews || [];

      let cursor = allData.nextUrl;

      while (cursor) {
        const moreData = await this.#fetchByUrl(cursor);
        if (!moreData || !moreData.reviews?.length) break;
        this.allReviews = [...this.allReviews, ...moreData.reviews];
        cursor = moreData.nextUrl;
      }

      // Hide loading state
      if (this.refs.loading) this.refs.loading.hidden = true;

      if (this.allReviews.length === 0) {
        if (this.hasAttribute('data-hide-if-empty')) {
          this.closest('.section-wrapper')?.setAttribute('hidden', '');
          return;
        }
        if (this.refs.emptyState) this.refs.emptyState.hidden = false;
        this.querySelector('.pr__overview')?.setAttribute('hidden', '');
        this.querySelector('.pr__controls')?.setAttribute('hidden', '');
        this.querySelector('.pr__divider')?.setAttribute('hidden', '');
        return;
      }

      // Has reviews — hide empty state, show content
      if (this.refs.emptyState) this.refs.emptyState.hidden = true;

      this.#renderAggregate();
      this.#renderMediaGallery();
      this.#renderFilterCounts();
      this.#injectStructuredData();
      this.#applyFiltersAndSort();
      this.#renderPage();
    } catch (error) {
      console.error('Failed to load reviews:', error);
      if (this.refs.loading) {
        this.refs.loading.textContent = this.t.unable_to_load || 'Unable to load reviews.';
      }
    }
  }

  async #fetchReviewsBatch(limit) {
    const params = new URLSearchParams({ limit: String(limit) });
    return fetchJSON(`${this.apiBase}/reviews?${params}`);
  }

  async #fetchByUrl(relativeUrl) {
    return fetchJSON(`https://api.okendo.io${relativeUrl}`);
  }

  // ── Filtering ──────────────────────────────────────────────────────────

  #getFilteredReviews() {
    let reviews = [...this.allReviews];

    if (this.filters.rating != null) {
      reviews = reviews.filter((r) => r.rating === this.filters.rating);
    }

    if (this.filters.media != null) {
      if (this.filters.media === 'photo') {
        reviews = reviews.filter((r) => r.media?.some((m) => m.type !== 'video'));
      } else if (this.filters.media === 'video') {
        reviews = reviews.filter((r) => r.media?.some((m) => m.type === 'video'));
      } else {
        reviews = reviews.filter((r) => r.media?.length > 0);
      }
    }

    if (this.filters.verified) {
      reviews = reviews.filter((r) => r.reviewer?.isVerified);
    }

    return reviews;
  }

  #applyFiltersAndSort() {
    const filtered = this.#getFilteredReviews();

    filtered.sort((a, b) => {
      if (this.currentSort === 'date') {
        const da = new Date(a.dateCreated);
        const db = new Date(b.dateCreated);
        return this.currentOrder === 'desc' ? db - da : da - db;
      }

      if (this.currentSort === 'rating') {
        return this.currentOrder === 'desc' ? b.rating - a.rating : a.rating - b.rating;
      }

      if (this.currentSort === 'helpfulness') {
        return this.currentOrder === 'desc'
          ? (b.helpfulCount || 0) - (a.helpfulCount || 0)
          : (a.helpfulCount || 0) - (b.helpfulCount || 0);
      }

      return 0;
    });

    this.sortedFiltered = filtered;
    this.displayedReviews = filtered.slice(0, this.perPage);
  }

  #renderFilterCounts() {
    const reviews = this.allReviews;
    const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    let photoCount = 0;
    let videoCount = 0;
    let anyMediaCount = 0;
    let verifiedCount = 0;

    for (const r of reviews) {
      if (r.rating >= 1 && r.rating <= 5) dist[r.rating]++;

      const hasMedia = r.media?.length > 0;
      if (hasMedia) anyMediaCount++;

      if (r.media?.some((m) => m.type !== 'video')) photoCount++;
      if (r.media?.some((m) => m.type === 'video')) videoCount++;
      if (r.reviewer?.isVerified) verifiedCount++;
    }

    for (let i = 1; i <= 5; i++) {
      const el = this.refs[`filterCount${i}`];
      if (el) el.textContent = dist[i] > 0 ? `(${dist[i]})` : '';
    }

    if (this.refs.filterCountPhoto) this.refs.filterCountPhoto.textContent = photoCount > 0 ? `(${photoCount})` : '';
    if (this.refs.filterCountVideo) this.refs.filterCountVideo.textContent = videoCount > 0 ? `(${videoCount})` : '';
    if (this.refs.filterCountAnyMedia)
      this.refs.filterCountAnyMedia.textContent = anyMediaCount > 0 ? `(${anyMediaCount})` : '';
    if (this.refs.filterCountVerified)
      this.refs.filterCountVerified.textContent = verifiedCount > 0 ? `(${verifiedCount})` : '';

    // Hide filter panels with zero results
    if (this.refs.mediaPanel && anyMediaCount === 0) this.refs.mediaPanel.hidden = true;
    if (this.refs.verifiedPanel && verifiedCount === 0) this.refs.verifiedPanel.hidden = true;
  }

  #renderActiveFilters() {
    if (!this.refs.activeFilters || !this.refs.activeFiltersList) return;

    const pills = [];

    if (this.filters.rating != null) {
      pills.push({ type: 'rating', label: this.filters.rating === 1 ? (this.t.filter_stars_one || '1 Star') : (this.t.filter_stars_other || `${this.filters.rating} Stars`).replace('2', String(this.filters.rating)) });
    }

    if (this.filters.media != null) {
      const labels = {
        photo: this.t.filter_with_photos || 'With Photos',
        video: this.t.filter_with_videos || 'With Videos',
        any: this.t.filter_with_media || 'With Media',
      };
      pills.push({ type: 'media', label: labels[this.filters.media] });
    }

    if (this.filters.verified) {
      pills.push({ type: 'verified', label: this.t.filter_verified_buyers || 'Verified Buyers' });
    }

    if (pills.length === 0) {
      this.refs.activeFilters.hidden = true;
      this.refs.activeFiltersList.innerHTML = '';
      return;
    }

    this.refs.activeFilters.hidden = false;
    this.refs.activeFiltersList.innerHTML = pills
      .map(
        (p) =>
          `<button class="pr__active-filter-pill" type="button" data-clear-filter="${p.type}">${this.#esc(p.label)} <span class="pr__active-filter-pill-x" aria-hidden="true">&times;</span></button>`
      )
      .join('');

    // Only show "Clear All" when multiple filters are active
    if (this.refs.clearAllBtn) {
      this.refs.clearAllBtn.hidden = pills.length < 2;
    }

    // Bind click listeners directly to each pill
    for (const pill of this.refs.activeFiltersList.querySelectorAll('.pr__active-filter-pill')) {
      pill.addEventListener('click', () => {
        const filterType = pill.dataset.clearFilter;
        if (!filterType) return;
        this.filters[filterType] = null;
        this.#updateFilterStatuses();
        this.#applyFiltersAndSort();
        this.#renderPage();
      });
    }
  }

  handleClearSingleFilter(e) {
    const pill = e.target.closest('.pr__active-filter-pill');
    if (!pill) return;

    const filterType = pill.dataset.clearFilter;
    if (!filterType) return;
    this.filters[filterType] = null;

    this.#updateFilterStatuses();
    this.#applyFiltersAndSort();
    this.#renderPage();
  }

  #updateFilterStatuses() {
    if (this.refs.ratingStatus) {
      this.refs.ratingStatus.textContent = this.filters.rating != null ? `${this.filters.rating} Stars` : '';
    }

    if (this.refs.mediaStatus) {
      const labels = { photo: 'Photos', video: 'Videos', any: 'Any' };
      this.refs.mediaStatus.textContent = this.filters.media != null ? labels[this.filters.media] : '';
    }

    if (this.refs.verifiedStatus) {
      this.refs.verifiedStatus.textContent = this.filters.verified ? 'Yes' : '';
    }

    // Update active classes on filter options
    for (const opt of this.querySelectorAll('.pr__filter-option[data-filter]')) {
      const filterType = opt.dataset.filter;
      const value = opt.dataset.value;
      let isActive = false;

      if (filterType === 'rating') isActive = this.filters.rating === parseInt(value, 10);
      else if (filterType === 'media') isActive = this.filters.media === value;
      else if (filterType === 'verified') isActive = this.filters.verified === true;

      opt.classList.toggle('pr__filter-option--active', isActive);
    }
  }

  // ── Panel close logic ──────────────────────────────────────────────────

  #closePanelOnOutsideClick(e) {
    for (const panel of this.querySelectorAll('.pr__filter-panel[open]')) {
      if (!panel.contains(e.target)) {
        panel.removeAttribute('open');
      }
    }
  }

  // ── Rendering ──────────────────────────────────────────────────────────

  #renderPage() {
    if (!this.refs.reviewsList) return;

    const prevCount = this.refs.reviewsList.querySelectorAll('.pr__review').length;
    const html = this.displayedReviews.map((r) => this.#renderReviewCard(r)).join('');
    this.refs.reviewsList.innerHTML = html;

    // Show more button visibility
    const hasMore = this.displayedReviews.length < this.sortedFiltered.length;

    if (this.refs.showMore) {
      this.refs.showMore.hidden = !hasMore;
    }

    // Animate newly added reviews (fade in / up)
    if (prevCount > 0) {
      const allCards = this.refs.reviewsList.querySelectorAll('.pr__review');

      for (let i = prevCount; i < allCards.length; i++) {
        allCards[i].classList.add('pr__review--entering');
      }

      requestAnimationFrame(() => {
        for (let i = prevCount; i < allCards.length; i++) {
          allCards[i].classList.remove('pr__review--entering');
          allCards[i].classList.add('pr__review--entered');
        }
      });
    }

    // Bind read-more buttons
    for (const btn of this.refs.reviewsList.querySelectorAll('.pr__review-read-more')) {
      btn.addEventListener('click', (e) => this.#handleReadMore(e));
    }

    // Bind review media clicks to open lightbox at that media item
    for (const el of this.refs.reviewsList.querySelectorAll('.pr__review-media-wrap')) {
      el.addEventListener('click', () => {
        const url = el.dataset.fullUrl;
        const type = el.dataset.mediaType;
        const idx = this.allMediaItems.findIndex((m) => m.fullUrl === url);
        this.#openLightbox(idx >= 0 ? idx : 0);
      });
    }

    this.#renderActiveFilters();
  }

  #renderAggregate() {
    const reviews = this.allReviews;
    const count = reviews.length;
    const sum = reviews.reduce((acc, r) => acc + (r.rating || 0), 0);
    const avg = count > 0 ? sum / count : 0;

    const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    for (const r of reviews) {
      if (r.rating >= 1 && r.rating <= 5) dist[r.rating]++;
    }

    const recommended = reviews.filter((r) => r.isRecommended).length;
    const recPct = count > 0 ? Math.round((recommended / count) * 100) : 0;

    if (this.refs.aggregateNumber) {
      this.refs.aggregateNumber.textContent = avg.toFixed(1);
    }

    if (this.refs.aggregateStars) {
      this.refs.aggregateStars.innerHTML = this.#renderStarsHTML(avg);
    }

    if (this.refs.reviewCount) {
      this.refs.reviewCount.textContent = count === 1
        ? (this.t.based_on_reviews_one || 'Based on 1 review').replace('1', String(count))
        : (this.t.based_on_reviews_other || `Based on ${count} reviews`).replace('2', String(count));
    }

    if (this.refs.recommend) {
      if (recommended > 0) {
        this.refs.recommend.textContent = (this.t.recommend_percentage || '{{ percentage }}% would recommend this product').replace('__PCT__', String(recPct));
      } else {
        this.refs.recommend.hidden = true;
      }
    }

    for (let i = 1; i <= 5; i++) {
      const bar = this.querySelector(`[ref="bar-${i}"]`);
      if (!bar) continue;
      const pct = count > 0 ? (dist[i] / count) * 100 : 0;
      bar.style.width = `${pct}%`;
    }
  }

  #renderMediaGallery() {
    if (!this.refs.mediaGallery || !this.showMediaGallery) return;

    this.allMediaItems = [];

    for (const review of this.allReviews) {
      if (!review.media?.length) continue;

      for (const m of review.media) {
        const isVideo = m.type === 'video';
        const thumbUrl = isVideo
          ? (m.thumbnailUrl || m.largeUrl || m.fullSizeUrl || '')
          : okendoImageSize(m.thumbnailUrl || m.largeUrl || m.fullSizeUrl || '', 400);
        const fullUrl = isVideo
          ? (m.fullSizeUrl || m.largeUrl || thumbUrl)
          : okendoImageSize(m.fullSizeUrl || m.largeUrl || thumbUrl, 2000);

        this.allMediaItems.push({
          type: isVideo ? 'video' : 'image',
          thumbUrl,
          fullUrl,
          alt: m.alt || (this.t.customer_review_media || 'Customer review media'),
          review: {
            name: review.reviewer?.displayName || (this.t.anonymous || 'Anonymous'),
            verified: review.reviewer?.isVerified || false,
            rating: review.rating || 0,
            title: review.title || '',
            body: review.body || '',
          },
        });
      }
    }

    if (this.allMediaItems.length === 0) {
      this.refs.mediaGallery.hidden = true;
      return;
    }

    const isMobile = window.innerWidth < 750;
    const maxThumbs = isMobile ? 9 : 4;
    const thumbs = this.allMediaItems.slice(0, maxThumbs);

    if (isMobile) {
      // Mobile: swiper carousel
      const slidesHTML = thumbs
        .map((m, idx) => {
          const thumb = this.#renderGalleryThumb(m, idx);
          return `<div class="swiper-slide pr__media-thumb-slide">${thumb}</div>`;
        })
        .join('');

      this.refs.mediaGallery.innerHTML = `
        <div class="pr__media-swiper swiper">
          <div class="swiper-wrapper">${slidesHTML}</div>
        </div>
      `;

      this.#initGallerySwiper();
    } else {
      // Desktop: static flex row, no swiper
      this.refs.mediaGallery.innerHTML = thumbs
        .map((m, idx) => this.#renderGalleryThumb(m, idx))
        .join('');
    }

    for (const thumb of this.refs.mediaGallery.querySelectorAll('.pr__media-thumb')) {
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.dataset.lightboxIndex, 10);
        this.#openLightbox(idx);
      });
    }
  }

  /**
   * Renders a single gallery thumbnail button
   * @param {Object} m - Media item
   * @param {number} idx - Index in allMediaItems
   * @returns {string} HTML string
   */
  #renderGalleryThumb(m, idx) {
    const isVideo = m.type === 'video';

    const mediaEl = isVideo
      ? `<video src="${this.#esc(m.thumbUrl)}" muted playsinline preload="metadata"></video>`
      : `<img src="${this.#esc(m.thumbUrl)}" alt="${this.#esc(m.alt)}" loading="lazy" width="190" height="190">`;

    const playOverlay = isVideo
      ? `<span class="pr__media-play" aria-hidden="true">${PLAY_SVG}</span>`
      : '';

    return `<button class="pr__media-thumb" type="button" aria-label="View ${isVideo ? 'video' : 'photo'}"
      data-lightbox-index="${idx}">${mediaEl}${playOverlay}</button>`;
  }

  async #initGallerySwiper() {
    const swiperEl = this.querySelector('.pr__media-swiper');
    if (!swiperEl) return;

    const Swiper = (await import('swiper')).default;

    this.gallerySwiper = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      grabCursor: true,
      watchSlidesProgress: true,
    });
  }

  #injectStructuredData() {
    const reviews = this.allReviews;
    if (!reviews.length) return;

    const productName = this.dataset.productName || '';
    const productUrl = this.dataset.productUrl || '';
    const productImage = this.dataset.productImage || '';
    const count = reviews.length;
    const sum = reviews.reduce((acc, r) => acc + (r.rating || 0), 0);
    const avg = count > 0 ? (sum / count).toFixed(2) : '0';

    const reviewItems = reviews.slice(0, 50).map((r) => {
      const item = {
        '@type': 'Review',
        author: { '@type': 'Person', name: r.reviewer?.displayName || (this.t.anonymous || 'Anonymous') },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: String(r.rating || 0),
          bestRating: '5',
          worstRating: '1',
        },
      };

      if (r.dateCreated) item.datePublished = r.dateCreated.split('T')[0];
      if (r.title) item.name = r.title;
      if (r.body) item.reviewBody = r.body;

      return item;
    });

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: productName,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: avg,
        reviewCount: String(count),
        bestRating: '5',
        worstRating: '1',
      },
      review: reviewItems,
    };

    if (productUrl) structuredData.url = productUrl;
    if (productImage) structuredData.image = productImage;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }

  #renderReviewCard(review) {
    const name = review.reviewer?.displayName || (this.t.anonymous || 'Anonymous');
    const initials = this.#getInitials(name);
    const verified = review.reviewer?.isVerified;
    const rating = review.rating || 0;
    const title = review.title || '';
    const body = review.body || '';
    const media = review.media || [];
    const attrs = review.reviewer?.attributes || [];
    const firstMedia = media[0];

    const truncated = body.length > this.bodyTruncateLength;
    const displayBody = truncated ? body.substring(0, this.bodyTruncateLength) + '...' : body;

    const hasMedia = firstMedia != null && this.showReviewMedia;

    let attrsHTML = '';

    if (attrs.length > 0) {
      const parts = attrs.map(
        (a) => `<span class="pr__review-attr-label">${this.#esc(a.label || a.name)}:</span> ${this.#esc(a.value)}`
      );
      attrsHTML = `<p class="pr__review-attributes">${parts.join(' | ')}</p>`;
    }

    let mediaHTML = '';

    if (hasMedia) {
      const isVideo = firstMedia.type === 'video';
      const thumbUrl = isVideo
        ? (firstMedia.thumbnailUrl || firstMedia.largeUrl || firstMedia.fullSizeUrl || '')
        : okendoImageSize(firstMedia.largeUrl || firstMedia.fullSizeUrl || firstMedia.thumbnailUrl || '', 400);
      const fullUrl = isVideo
        ? (firstMedia.fullSizeUrl || firstMedia.largeUrl || thumbUrl)
        : okendoImageSize(firstMedia.fullSizeUrl || firstMedia.largeUrl || firstMedia.thumbnailUrl || '', 2000);

      const mediaEl = isVideo
        ? `<video src="${this.#esc(thumbUrl)}" muted playsinline preload="metadata"></video>`
        : `<img src="${this.#esc(thumbUrl)}" alt="${this.#esc(firstMedia.alt || (this.t.review_photo_by || 'Review photo by __NAME__').replace('__NAME__', name))}" loading="lazy">`;

      const playOverlay = isVideo
        ? `<span class="pr__media-play" aria-hidden="true">${PLAY_SVG}</span>`
        : '';

      mediaHTML = `<div class="pr__review-media pr__review-media-wrap" data-full-url="${this.#escAttr(fullUrl)}" data-media-type="${isVideo ? 'video' : 'image'}" role="button" tabindex="0" aria-label="${isVideo ? (this.t.view_video || 'View video') : (this.t.view_photo || 'View photo')}">${mediaEl}${playOverlay}</div>`;
    }

    return `
      <article class="pr__review" role="listitem">
        <div class="pr__review-header">
          ${review.reviewer?.avatarUrl
            ? `<div class="pr__review-avatar" aria-hidden="true"><img src="${this.#esc(review.reviewer.avatarUrl)}" alt="" loading="lazy" width="36" height="36"></div>`
            : `<div class="pr__review-avatar pr__review-avatar--initials" aria-hidden="true">${initials}</div>`
          }
          <div class="pr__review-meta">
            <span class="pr__review-name">${this.#esc(name)}</span>
            ${
              verified
                ? `<span class="pr__review-verified">
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm3.41 5.29a1 1 0 0 0-1.42 0L7 8.17 6.01 7.17a1 1 0 1 0-1.42 1.42l1.5 1.5a1 1 0 0 0 1.42 0l3.5-3.5a1 1 0 0 0 .4-1.3z"/></svg>
                ${this.t.verified_buyer || 'Verified Buyer'}
              </span>`
                : ''
            }
            ${attrsHTML}
          </div>
        </div>

        <div class="pr__review-stars" aria-label="${(this.t.rating_out_of_5 || '__RATING__ out of 5 stars').replace('__RATING__', String(rating))}">
          ${this.#renderStarsHTML(rating)}
        </div>

        <div class="pr__review-content">
          <h3 class="pr__review-title">${this.#esc(title)}</h3>
          <p class="pr__review-body" data-full-text="${this.#escAttr(body)}">${this.#esc(displayBody)}</p>
          ${truncated ? `<button class="pr__review-read-more" type="button">${this.t.read_more || 'Read More'}</button>` : ''}
        </div>
        ${mediaHTML}
      </article>
    `;
  }

  #renderStarsHTML(rating) {
    let html = '';
    const fullStars = Math.floor(rating);
    const decimal = rating - fullStars;
    const hasHalf = decimal >= 0.3 && decimal <= 0.7;
    const roundedUp = decimal > 0.7 ? 1 : 0;
    const filledCount = fullStars + roundedUp;

    for (let i = 1; i <= 5; i++) {
      let cls = 'pr__star-empty';

      if (i <= filledCount) {
        cls = 'pr__star-filled';
      } else if (hasHalf && i === filledCount + 1) {
        cls = 'pr__star-half';
      }

      html += `<svg class="${cls}" viewBox="0 0 32 32" aria-hidden="true"><use xlink:href="#pr-star"></use></svg>`;
    }

    return html;
  }

  #getInitials(name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  #esc(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  #escAttr(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── Event handlers ─────────────────────────────────────────────────────

  #handleReadMore(e) {
    const btn = e.currentTarget;
    const bodyEl = btn.previousElementSibling;
    if (!bodyEl) return;
    bodyEl.textContent = bodyEl.dataset.fullText;
    btn.remove();
  }

  handleFilterOption(e) {
    const btn = e.target.closest('.pr__filter-option');
    if (!btn) return;

    const filterType = btn.dataset.filter;
    const value = btn.dataset.value;

    if (filterType === 'rating') {
      const numVal = parseInt(value, 10);
      this.filters.rating = this.filters.rating === numVal ? null : numVal;
    } else if (filterType === 'media') {
      this.filters.media = this.filters.media === value ? null : value;
    } else if (filterType === 'verified') {
      this.filters.verified = this.filters.verified ? null : true;
    }

    // Close the panel
    btn.closest('.pr__filter-panel')?.removeAttribute('open');

    this.#updateFilterStatuses();
    this.#applyFiltersAndSort();
    this.#renderPage();
  }

  handleSortOption(e) {
    const btn = e.target.closest('.pr__filter-option');
    if (!btn) return;

    const val = btn.dataset.sort;
    const [sort, order] = val.split('-');
    this.currentSort = sort;
    this.currentOrder = order;

    // Update active state on sort options
    for (const opt of this.querySelectorAll('.pr__filter-option[data-sort]')) {
      opt.classList.toggle('pr__filter-option--active', opt.dataset.sort === val);
    }

    if (this.refs.sortStatus) this.refs.sortStatus.textContent = btn.textContent;

    btn.closest('.pr__filter-panel')?.removeAttribute('open');

    this.#applyFiltersAndSort();
    this.#renderPage();
  }

  handleWriteReview() {
    if (!window.okeWidgetApi) {
      window.open(
        `https://reviews.okendo.io/stores/${this.subscriberId}/review?productId=shopify-${this.productId}`,
        '_blank'
      );
    }
  }

  handleShowMore() {
    const nextCount = this.displayedReviews.length + this.perPage;
    this.displayedReviews = this.sortedFiltered.slice(0, nextCount);
    this.#renderPage();
  }

  handleDistributionFilter(e) {
    const row = e.target.closest('.pr__distribution-row');
    if (!row) return;

    const rating = parseInt(row.dataset.rating, 10);
    this.filters.rating = this.filters.rating === rating ? null : rating;

    this.#updateFilterStatuses();
    this.#applyFiltersAndSort();
    this.#renderPage();
  }

  handleClearAllFilters() {
    this.filters.rating = null;
    this.filters.media = null;
    this.filters.verified = null;

    this.#updateFilterStatuses();
    this.#applyFiltersAndSort();
    this.#renderPage();
  }

  // ── Lightbox ───────────────────────────────────────────────────────────

  async #openLightbox(startIndex = 0) {
    if (this.allMediaItems.length === 0 || !this.enableLightbox) return;

    // Destroy any existing lightbox
    this.#destroyLightbox();

    const dialog = document.createElement('dialog');
    dialog.className = 'pr__lightbox';
    this.lightboxDialog = dialog;

    // Build slides
    const popoverClass = this.popoverColorClass;

    const slidesHTML = this.allMediaItems
      .map((m) => {
        const r = m.review;
        const reviewCard = r
          ? `<div class="pr__lightbox-review ${popoverClass}">
              <div class="pr__lightbox-review-header">
                <span class="pr__review-name">${this.#esc(r.name)}</span>
                ${r.verified ? `<span class="pr__review-verified"><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm3.41 5.29a1 1 0 0 0-1.42 0L7 8.17 6.01 7.17a1 1 0 1 0-1.42 1.42l1.5 1.5a1 1 0 0 0 1.42 0l3.5-3.5a1 1 0 0 0 .4-1.3z"/></svg> ${this.t.verified_buyer || 'Verified Buyer'}</span>` : ''}
              </div>
              <div class="pr__review-stars" aria-label="${(this.t.rating_out_of_5 || '__RATING__ out of 5 stars').replace('__RATING__', String(r.rating))}">${this.#renderStarsHTML(r.rating)}</div>
              ${r.title ? `<h3 class="pr__review-title">${this.#esc(r.title)}</h3>` : ''}
              ${r.body ? `<p class="pr__lightbox-review-body">${this.#esc(r.body.length > 200 ? r.body.substring(0, 200) + '...' : r.body)}</p>` : ''}
            </div>`
          : '';

        if (m.type === 'video') {
          return `<div class="swiper-slide pr__lightbox-slide">
            <div class="pr__lightbox-slide-content">
              <video src="${this.#esc(m.fullUrl)}" controls playsinline preload="metadata" class="pr__lightbox-media pr__lightbox-media--loaded"></video>
              <span class="pr__lightbox-video-play" aria-hidden="true">${PLAY_SVG}</span>
            </div>
            ${reviewCard}
          </div>`;
        }
        return `<div class="swiper-slide pr__lightbox-slide">
          <div class="pr__lightbox-slide-content">
            <span class="pr__lightbox-spinner"></span>
            <img src="${this.#esc(m.fullUrl)}" alt="${this.#esc(m.alt)}" class="pr__lightbox-media" loading="lazy">
          </div>
          ${reviewCard}
        </div>`;
      })
      .join('');

    // Build thumbnails
    const thumbsHTML = this.allMediaItems
      .map((m, idx) => {
        const isVideo = m.type === 'video';
        const thumbSrc = isVideo ? m.thumbUrl : okendoImageSize(m.thumbUrl, 120);
        const mediaEl = isVideo
          ? `<video src="${this.#esc(thumbSrc)}" muted playsinline preload="metadata"></video>`
          : `<img src="${this.#esc(thumbSrc)}" alt="" loading="lazy">`;
        const playIcon = isVideo ? `<span class="pr__media-play pr__media-play--sm" aria-hidden="true">${PLAY_SVG}</span>` : '';
        return `<button class="pr__lightbox-thumb ${idx === startIndex ? 'pr__lightbox-thumb--active' : ''}" type="button" data-slide-index="${idx}" aria-label="${(this.t.go_to_media || 'Go to media __INDEX__').replace('__INDEX__', String(idx + 1))}">${mediaEl}${playIcon}</button>`;
      })
      .join('');

    dialog.innerHTML = `
      <div class="pr__lightbox-backdrop"></div>
      <div class="pr__lightbox-inner">
        <button class="pr__lightbox-close" type="button" aria-label="${this.t.close_lightbox || 'Close lightbox'}">${CLOSE_SVG}</button>

        <div class="pr__lightbox-main">
          <button class="pr__lightbox-arrow pr__lightbox-arrow--prev flip-x" type="button" aria-label="${this.t.previous || 'Previous'}">
            <span class="pr__lightbox-arrow-icon">${ARROW_SVG}</span>
          </button>

          <div class="pr__lightbox-swiper swiper">
            <div class="swiper-wrapper">${slidesHTML}</div>
          </div>

          <button class="pr__lightbox-arrow pr__lightbox-arrow--next" type="button" aria-label="${this.t.next || 'Next'}">
            <span class="pr__lightbox-arrow-icon">${ARROW_SVG}</span>
          </button>
        </div>

        <div class="pr__lightbox-thumbs">${thumbsHTML}</div>
      </div>
    `;

    document.body.appendChild(dialog);

    // Show dialog with animation
    dialog.showModal();
    requestAnimationFrame(() => dialog.classList.add('pr__lightbox--open'));

    // Init Swiper
    const Swiper = (await import('swiper')).default;

    this.lightboxSwiper = new Swiper(dialog.querySelector('.pr__lightbox-swiper'), {
      slidesPerView: 1,
      initialSlide: startIndex,
      speed: 400,
      grabCursor: true,
      autoHeight: true,
      loop: true,
    });

    // Handle image loading — show spinner until loaded, then fade in
    for (const img of dialog.querySelectorAll('.pr__lightbox-media:not(video)')) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('pr__lightbox-media--loaded');
        img.previousElementSibling?.remove();
      } else {
        img.addEventListener('load', () => {
          img.classList.add('pr__lightbox-media--loaded');
          img.previousElementSibling?.remove();
        }, { once: true });
      }
    }

    // Keyboard navigation: arrow keys for slides, escape to close
    const handleKeydown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.lightboxSwiper?.slidePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.lightboxSwiper?.slideNext();
      }
    };
    dialog.addEventListener('keydown', handleKeydown);

    // Focus the dialog for keyboard events
    dialog.focus();

    // Sync thumbnails with swiper
    this.lightboxSwiper.on('slideChange', () => {
      const idx = this.lightboxSwiper.realIndex;

      for (const t of dialog.querySelectorAll('.pr__lightbox-thumb')) {
        t.classList.toggle('pr__lightbox-thumb--active', parseInt(t.dataset.slideIndex, 10) === idx);
      }

      // Pause all videos when switching slides
      for (const v of dialog.querySelectorAll('video')) {
        v.pause();
      }

      // Scroll active thumb into view
      const activeThumb = dialog.querySelector('.pr__lightbox-thumb--active');
      if (activeThumb) activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    });

    // Thumb clicks
    for (const thumb of dialog.querySelectorAll('.pr__lightbox-thumb')) {
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.dataset.slideIndex, 10);
        this.lightboxSwiper.slideTo(idx);
      });
    }

    // Video play buttons inside lightbox
    for (const playBtn of dialog.querySelectorAll('.pr__lightbox-video-play')) {
      playBtn.addEventListener('click', () => {
        const video = playBtn.previousElementSibling;
        if (video?.tagName === 'VIDEO') {
          video.play();
          playBtn.hidden = true;
        }
      });
    }

    // Arrow buttons
    dialog.querySelector('.pr__lightbox-arrow--prev')?.addEventListener('click', () => {
      this.lightboxSwiper.slidePrev();
    });
    dialog.querySelector('.pr__lightbox-arrow--next')?.addEventListener('click', () => {
      this.lightboxSwiper.slideNext();
    });

    // Close button
    dialog.querySelector('.pr__lightbox-close')?.addEventListener('click', () => {
      this.#closeLightbox();
    });

    // Click backdrop or empty area to close
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.classList.contains('pr__lightbox-backdrop') || e.target.classList.contains('pr__lightbox-inner')) {
        this.#closeLightbox();
      }
    });

    // Escape key
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.#closeLightbox();
    });
  }

  #closeLightbox() {
    if (!this.lightboxDialog) return;

    // Pause all videos immediately
    for (const v of this.lightboxDialog.querySelectorAll('video')) {
      v.pause();
    }

    // Trigger fade-out: remove open class
    this.lightboxDialog.classList.remove('pr__lightbox--open');

    // Wait for the CSS opacity transition to finish before removing from DOM
    const duration = 250;
    setTimeout(() => {
      this.#destroyLightbox();
    }, duration);
  }

  #destroyLightbox() {
    if (this.lightboxSwiper) {
      this.lightboxSwiper.destroy(true, true);
      this.lightboxSwiper = null;
    }

    if (this.lightboxDialog) {
      this.lightboxDialog.close();
      this.lightboxDialog.remove();
      this.lightboxDialog = null;
    }
  }
}

customElements.define('product-reviews-component', ProductReviews);
